'use client';

import { useEffect } from 'react';

import { useSrpPageViewCoordinator } from './SrpPageViewCoordinator';

type SrpZeroResultSearchState = {
  /** The key `useSrpLiveSearchPageView` returned for the same search. */
  requestKey: string;
  /**
   * True only for a settled search whose own result set is empty. The caller
   * owns that judgement: a page past the last one is empty without the search
   * being empty, and placeholder data belongs to the previous request.
   */
  isZeroResult: boolean;
  /** Agentic sessions are excluded from both sides of the rate. */
  isAgentic: boolean;
};

/**
 * Emits the zero-result search event once per search that returns nothing.
 *
 * Keyed on the same request key as the SRP page view, and deduped by the same
 * coordinator, so the rate this feeds has a numerator and a denominator that
 * count the same notion of "a search" — a re-render, a view-mode toggle, an
 * equivalent request object, or the slug morph's remount is not a second
 * search.
 */
export function useSrpZeroResultSearch({ requestKey, isZeroResult, isAgentic }: SrpZeroResultSearchState): void {
  const { trackZeroResultSearch } = useSrpPageViewCoordinator();

  useEffect(() => {
    trackZeroResultSearch(requestKey, isZeroResult, isAgentic);
  }, [isAgentic, isZeroResult, requestKey, trackZeroResultSearch]);
}
